import { GzPolicyUiSettingsGridItemFormWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/form';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { RequiredValidator } from '../../../../../../../../ui/gzForm/validators/required.validator';
import { getPathValidatorsByType, MAX_LENGTH_REMARKS } from './utils/validators';
import { customElement, query } from 'lit/decorators.js';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../../../../../../ui/gzDropdown';
import { GzInputWebComponent } from '../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { FormGroup } from '../../../../../../../../ui/gzForm/formGroup/form-group';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_EXCLUSIONS_ITEM } from '../../../../../../../../webcomponents.constants';
import {
  AntimalwareSettingsExclusionsModuleType,
  AntimalwareSettingsExclusionsTaskType,
} from '@nirvana/shared/dtos/types/policies/common';
import { MaxLengthValidator } from '../../../../../../../../ui/gzForm/validators/max-length.validator';
import {
  getAntimalwareSettingsExclusionsModuleTypeFormOptions,
  getRestrictedAntimalwareSettingsExclusionsTaskTypeFormOptions,
} from '../../../../../utils/uiSettings/antimalware/antimalware.values';
import { GzEnumFilterWebComponent } from '../../../../../../../../ui/gzEnumFilter';
import { Feature } from '@nirvana/shared/constants';
import { ListItem } from '../../../../../../../../ui/gzSingleSelectList/types';
import { EnumListItem } from '../../../../../../../../ui/gzEnumList/types';
import { getAvailableModuleOptionsByType, getPathPlaceholderByType } from './utils/configs';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_EXCLUSIONS_ITEM)
export class GzAntimalwareExclusionsItemFormWebComponent extends GzPolicyUiSettingsGridItemFormWebComponent {
  @query('#excludedItemType')
  excludedItemType: GzFormFieldWebComponent<GzDropdownWebComponent>;

  @query('#excludedItemEntity')
  excludedItemEntity: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#excludedItemModules')
  excludedItemModules: GzFormFieldWebComponent<GzEnumFilterWebComponent>;

  @query('#excludedItemRemarks')
  excludedItemRemarks: GzFormFieldWebComponent<GzInputWebComponent, string>;

  public setError(error: string) {
    this.excludedItemType.setError(error);
    this.excludedItemEntity.setError(error);

    this.excludedItemModules.setError(error);
    this.excludedItemModules.Input.isError = true;
    this.excludedItemModules.Input.errorMessage = error;
  }

  renderFormFields(): TemplateResult {
    return html`
      <div class="policy-grid-item-form--field">
        <gz-form-field id="excludedItemType">
          <gz-single-select
            cannotDelete
            gz-form-input
            label="${translateFn('Type')}"
            .value=${
              Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
                ? AntimalwareSettingsExclusionsTaskType.FOLDER
                : AntimalwareSettingsExclusionsTaskType.FILE
            }
            .filterOptions=${getRestrictedAntimalwareSettingsExclusionsTaskTypeFormOptions()}
            @selectionChange="${this.handleTypeFieldChange}"
            @open="${this.resetErrorState}"
          ></<gz-single-select>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field w-full">
        <gz-form-field id="excludedItemEntity">
          <gz-input
            gz-form-input
            label="${translateFn('Excluded items')}"
            placeholder="${translateFn('Type here')}"
            @change="${e => {
              this.resetErrorStateForType();
              this.resetErrorStateForModule();
            }}"
          ></gz-input>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field">
        <gz-form-field id="excludedItemModules">
          <gz-enum-filter
            gz-form-input
            label="${translateFn('Modules')}"
            placeholder="${translateFn('Select module')}"
            allItemsLabel="${translateFn('All modules')}"
            confirmActionButtonString="${translateFn('Apply')}"
            .filterOptions="${getAntimalwareSettingsExclusionsModuleTypeFormOptions()}"
            @invalidOpen="${this.resetErrorState}"
          ></gz-enum-filter>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field w-full">
        <gz-form-field id="excludedItemRemarks">
          <gz-input
            gz-form-input
            label="${translateFn('Description')}"
            placeholder="${translateFn('Enter a description')}"
          ></gz-input>
        </gz-form-field>
      </div>
    `;
  }

  private resetErrorStateForModule() {
    this.excludedItemModules.Input.isError = false;
    this.excludedItemModules.validate();
  }

  private resetErrorStateForType() {
    this.excludedItemType.validate();
  }

  private resetErrorState() {
    this.resetErrorStateForType();
    this.excludedItemEntity.validate();
    this.resetErrorStateForModule();
  }

  createFormGroup(): void {
    this.excludedItemType.Validators = [RequiredValidator('')];
    this.excludedItemRemarks.Validators = [
      MaxLengthValidator(
        MAX_LENGTH_REMARKS,
        translateFn('The description must have a maximum of {maxLength} characters.', { maxLength: MAX_LENGTH_REMARKS })
      ),
    ];

    this.formGroup = new FormGroup({
      taskType: this.excludedItemType,
      path: this.excludedItemEntity,
      module: this.excludedItemModules,
      remarks: this.excludedItemRemarks,
    });
  }

  private handleTypeFieldChange() {
    const filterOptions: ListItem[] = getAntimalwareSettingsExclusionsModuleTypeFormOptions();
    this.excludedItemEntity.Input.placeholder = getPathPlaceholderByType(
      this.excludedItemType.value as AntimalwareSettingsExclusionsTaskType
    );

    this.excludedItemEntity.Validators = getPathValidatorsByType(
      this.excludedItemType.value as AntimalwareSettingsExclusionsTaskType
    );
    this.excludedItemEntity.validate();

    // set available options for module
    const availableOptions = getAvailableModuleOptionsByType(
      this.excludedItemType.value as AntimalwareSettingsExclusionsTaskType
    );
    this.excludedItemModules.Input.filterOptions = filterOptions.filter(option =>
      availableOptions.includes(option.value as AntimalwareSettingsExclusionsModuleType)
    ) as EnumListItem[];
    this.excludedItemModules.value = availableOptions;
  }

  resetForm(resetType = true): void {
    if (resetType) {
      this.excludedItemType.value = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
        ? AntimalwareSettingsExclusionsTaskType.FOLDER
        : AntimalwareSettingsExclusionsTaskType.FILE;
      this.excludedItemType.resetField();
    }

    this.excludedItemEntity.value = '';
    this.excludedItemEntity.resetField();

    this.excludedItemRemarks.value = '';
    this.excludedItemRemarks.resetField();
  }

  reset() {
    this.resetForm();
  }

  resetOnAdd() {
    this.resetForm(false);
  }
}
